import { take, call, put, select, spawn, cancel } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { deepCopy, forwardTo, isDefined, mergeConfigs, mergeTranslate } from '../../lib/utils';
import api from '../../lib/api';
import { sortLocations } from '../../lib/utils';
import {
  LOADING,
  REQUEST_ERROR,
  SEND_FIREBASE_TOKEN,
  INIT,
  SET_COMMON_PROP,
  GET_SOCIALS,
  SEND_FEEDBACK,
  SET_COMMON_MODAL,
  GET_TERMS,
  GET_PRIVACY_POLICY,
  GET_FAQ,
  INIT_FIREBASE,
  LOCATION,
  INIT_FIREBASE_DATABASE,
  GET_ALLERGENS,
  GET_ORDER_PRODUCTION_MINS,
  SET_NAVCONFIG,
  START_TIMER,
  STOP_TIMER,
  RESTART_TIMER,
  PAUSE_TIMER,
  CONTINUE_TIMER,
} from './constants';
import { UPDATE_PROFILE, GET_PROFILE, GET_VOUCHERS } from '../profile/constants';
import { GET_ORDER_HISTORY, SET_ORDERS_PROP } from '../orders/constants';
import { SET_RESTAURANT_PROP, GET_RESTAURANTS_SNOOZED_DATA } from '../restaurants/constants';
import { Plugins, Capacitor } from '@capacitor/core';
import { translate } from '../../lib/translate';
import { setCatalog } from '../../translationCatalogWrapper';
import { updateConfig, getConfig } from '../../appConfig';
import { restoreAuthSaga, startPeriodicalSaga } from '../profile/sagas';
import { errorHandlerSaga } from '../sagas';
import firebase from 'firebase/app';
import 'firebase/database'; // If using Firebase database
import 'firebase/storage'; // If using Firebase storage
import asyncStorage from '../../lib/asyncStorage';
import Stripe from '../../lib/stripe';
import Basket from '../../lib/basket';
import WebFont from 'webfontloader';
import { initRouter } from '../../navConfig';
import { filterMenu } from '../restaurants/sagas';
import delay from '@redux-saga/delay-p';
import { updateKioskData } from '../actions';

const { SplashScreen, PushNotifications, Device } = Plugins;

export const loading = function* (fn = null) {
  try {
    if (!isDefined(fn)) {
      return;
    }
    yield put({ type: LOADING, loading: true });
    if (fn) {
      yield call(fn);
    }
  } finally {
    yield put({ type: LOADING, loading: false });
  }
};

/**
 * send firebase token saga
 */
// TO DO: CHECK THIS (AP)
export const sendFirebaseToken = function* () {
  while (true) {
    const request = yield take(SEND_FIREBASE_TOKEN);
    yield call(loading, function* () {
      try {
        yield call(api.sendFirebaseToken, request.args);
      } catch (error) {
        yield put({ type: REQUEST_ERROR, error: error.message });
      }
    });
  }
};

/* On init app */
export const initSaga = function* () {
  while (true) {
    yield take(INIT);
    yield call(api.createAxiosInstance);
    const navConfig = yield call(initRouter);
    yield put({ type: SET_NAVCONFIG, data: navConfig });

    const { hasOrdering } = getConfig().appType;

    // get config for front-end from BO
    const config = yield call(api.getFrontEndAppConfig);
    yield put({ type: SET_COMMON_PROP, key: 'clientStyles', value: config.styles });

    const frontEndAppConfig = {
      ...config.front_end_app_config,
      timezone: config.timezone || 'Europe/London',
    };
    const client = yield call(api.getClient);
    document.title = client.buisiness_name;
    const favicon_image = client.favicon_image;
    const favicon = document.getElementById('favicon');
    favicon.href = favicon_image;
    yield put({ type: SET_COMMON_PROP, key: 'clientProfile', value: client });
    yield put(updateKioskData({}));

    if (getConfig().payment === 'judopay') {
      console.log('judopay');
    } else {
      // get public stripe key from BO
      if (hasOrdering) {
        const publicStripeKey = yield call(api.getPublicStripeKey);
        frontEndAppConfig.services.stripe_key = publicStripeKey;
        //init stripe plugin
        yield call(Stripe.setStripePublishableKey, publicStripeKey);
      }
    }

    setCustomDesign(config.styles, config.custom_css, config.images);

    const mergedConfig = mergeConfigs(frontEndAppConfig, getConfig());

    updateConfig(mergedConfig);

    // get device language
    if (!mergedConfig.localization) {
      mergedConfig.localization = {};
      updateConfig(mergedConfig);
    }
    const localization = getConfig().localization;
    const deviceLanguageCode = yield call(Device.getLanguageCode);
    const sysLocale = deviceLanguageCode.value.substr(0, 2);
    if (
      Capacitor.platform !== 'web' &&
      localization &&
      localization.supportedLocales &&
      localization.supportedLocales[sysLocale]
    ) {
      mergedConfig.localization.defaultLocale = sysLocale;
      updateConfig(mergedConfig);
    }

    let translationCatalog = {};
    try {
      translationCatalog = yield call(api.getTranslations);
    } catch (error) {
      // xxx
    }

    let translationCatalogFromAdmin =
      frontEndAppConfig && frontEndAppConfig.client && frontEndAppConfig.client.translation_json
        ? frontEndAppConfig.client.translation_json
        : {};

    const mergedTranslate = mergeTranslate(translationCatalogFromAdmin, translationCatalog);
    yield call(setCatalog, mergedTranslate);
    yield put({
      type: SET_COMMON_PROP,
      key: 'translationCatalog',
      value: mergedTranslate,
    });

    // remove all console.log messages if appConfig.general.debug is FALSE
    if (console && isDefined(getConfig().general.debug) && !getConfig().general.debug) {
      // don't try this at home :)
      // eslint-disable-next-line no-console
    }

    yield call(restoreAuthSaga);

    //add firebase listeners for push notification
    yield put({ type: INIT_FIREBASE });
    //add firebase listeners for database
    try {
      yield spawn(firebaseDatabase);
    } catch (e) {
      yield call(errorHandlerSaga, e);
    }

    yield call(startPeriodicalSaga);
    //add app version
    const appVersion = yield call(api.getAppVersion);
    yield put({ type: SET_COMMON_PROP, key: 'appVersion', value: appVersion });

    const deliveryRangeType = yield call(api.getDeliveryRangeType);
    yield put({
      type: SET_COMMON_PROP,
      key: 'deliveryRangeType',
      value: deliveryRangeType,
    });

    let kioskData = {};
    try {
      let savedKioskData = yield call(asyncStorage.getItem, 'kioskData');
      if (savedKioskData) {
        kioskData = JSON.parse(savedKioskData);
      }
    } catch (error) {
      console.log('Error getting saved kiosk data', error);
    }

    let defaultMenuId = 0;
    if (kioskData && kioskData?.kiosk_default_menu_id) {
      defaultMenuId = kioskData.kiosk_default_menu_id;
    } else {
      defaultMenuId = yield call(api.getDefaultMenuId);
    }
    yield put({
      type: SET_COMMON_PROP,
      key: 'defaultMenuId',
      value: defaultMenuId,
    });

    try {
      const defaultMenu = yield call(api.getDefaultMenu, defaultMenuId);
      yield put({
        type: SET_RESTAURANT_PROP,
        key: 'defaultMenu',
        value: { ...defaultMenu, menuName: `${defaultMenu.menuName} Default` },
        merge: true,
      });
    } catch (e) {
      // xxx
    }

    const restaurants = yield call(api.getRestaurants);
    yield put({
      type: SET_RESTAURANT_PROP,
      key: 'restaurants',
      value: restaurants,
    });

    const allergens = yield call(api.getAllergens);
    yield put({
      type: SET_RESTAURANT_PROP,
      key: 'allergens',
      value: allergens,
    });

    yield put({ type: GET_ORDER_PRODUCTION_MINS });

    if (hasOrdering) {
      yield call(Basket.import);
    }
    if (Basket.getMenu() && Basket.items.length > 0) {
      let businessLocationId = Basket.getRestaurant().business_location_id;
      let ikentooMenu = yield call(api.getIkenooMenu, Basket.getMenu(), businessLocationId);
      ikentooMenu = yield call(filterMenu, deepCopy(ikentooMenu));
      yield put({
        type: SET_RESTAURANT_PROP,
        key: 'ikentooMenu',
        value: ikentooMenu,
      });
    }
    SplashScreen.hide();
    yield put({ type: SET_COMMON_PROP, key: 'initLoading', value: false });
  }
};

const setCustomDesign = (styles, customCss, images) => {
  let documentStyle = document.createElement('style');
  let css = customCss;

  const themeWrapper = document.querySelector('body');

  if (styles.colors) {
    Object.entries(styles.colors).map(([key, value]) => {
      themeWrapper.style.setProperty(key, value);
      return true;
    });
  }

  // if (styles.fontSizes) {
  //   Object.entries(styles.fontSizes).map(([key, value]) => {
  //     themeWrapper.style.setProperty(key, value);
  //     return true;
  //   });
  // }

  if (styles.fonts) {
    Object.entries(styles.fonts).map(([key, value]) => {
      WebFont.load({
        google: {
          families: [value],
        },
      });
      themeWrapper.style.setProperty(key, value);
      return true;
    });
  }

  if (styles.isRoundButton) {
    css += `ion-button, .delivery-options-menu ion-item {
      --border: 1px solid;
      --border-radius: 20px;
      border-radius: 20px;
    }`;
  }

  setCustomImages(images, documentStyle, css);
};

const setCustomImages = (images, sheet, css) => {
  if (images.favicon_image) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = images.favicon_image;
  }

  if (images.main_image) {
    css += `
    .web .ion-page > ion-content {
      background-image: url(${images.main_image});
      background-repeat: no-repeat;
      background-position: 50% 0;
      background-size: cover;
    }
    `;
  }

  sheet.innerHTML = css;
  document.body.appendChild(sheet);
};

/* social Saga */
export const socialSagaFlow = function* () {
  while (true) {
    yield take(GET_SOCIALS);
    //get socials
    yield call(loading, function* () {
      const social = yield call(api.getSocialLinks);
      yield put({ type: SET_COMMON_PROP, key: 'social', value: social });
    });
  }
};

/* send Feedback Saga */
export const sendFeedbackSaga = function* () {
  while (true) {
    const action = yield take(SEND_FEEDBACK);
    yield call(loading, function* () {
      const {
        food,
        service,
        experience,
        venue_score,
        commentService,
        commentTech,
        commentExperience,
        selectedRestaurant,
        customerService,
        isKiosk,
        order_type,
      } = action.data;
      const customerServicePayload = {
        selected_restaurant: selectedRestaurant,
        feedback_type: 'app customer_service',
        food_score: food,
        service_score: service,
        feedback_response: commentService,
        venue_score,
        is_kiosk: isKiosk,
        order_type,
      };
      const techServicePayload = {
        selected_restaurant: selectedRestaurant,
        feedback_type: 'app tech_support',
        feedback_response: commentTech,
        is_kiosk: isKiosk,
        order_type,
      };
      const kioskFeedbackPayload = {
        selected_restaurant: selectedRestaurant,
        feedback_type: 'app kiosk_feedback',
        experience_score: experience,
        feedback_response: commentExperience,
        is_kiosk: isKiosk,
        order_type,
      };
      //send feedback
      if (customerService) {
        yield call(api.sendFeedback, customerServicePayload);
      } else if (isKiosk) {
        yield call(api.sendFeedback, kioskFeedbackPayload);
      } else {
        yield call(api.sendFeedback, techServicePayload);
      }
      yield put({
        type: SET_COMMON_MODAL,
        modal: 'isFeedbackModalOpen',
        value: true,
      });
    });
  }
};

/* terms Saga */
export const getTermsFlow = function* () {
  while (true) {
    yield take(GET_TERMS);
    const client_name = yield select((store) => store.common.clientProfile.name);
    const locale = yield call(getLocaleFlow);
    yield call(loading, function* () {
      const terms = yield call(api.getTerms, locale, client_name);
      yield put({ type: SET_COMMON_PROP, key: 'terms', value: terms });
    });
  }
};

/* policy Saga */
export const getPrivacyPolicyFlow = function* () {
  while (true) {
    yield take(GET_PRIVACY_POLICY);
    const client_name = yield select((store) => store.common.clientProfile.name);
    const locale = yield call(getLocaleFlow);
    yield call(loading, function* () {
      const privacyPolicy = yield call(api.getPrivacyPolicy, locale, client_name);
      yield put({
        type: SET_COMMON_PROP,
        key: 'privacyPolicy',
        value: privacyPolicy,
      });
    });
  }
};

/* faq Saga */
export const getFaqFlow = function* () {
  while (true) {
    yield take(GET_FAQ);
    const client_name = yield select((store) => store.common.clientProfile.name);
    const locale = yield call(getLocaleFlow);
    yield call(loading, function* () {
      const faq = yield call(api.getFaq, locale, client_name);
      yield put({ type: SET_COMMON_PROP, key: 'faq', value: faq });
    });
  }
};

/* allergens Saga */
export const getAllergensInfoFlow = function* () {
  while (true) {
    yield take(GET_ALLERGENS);
    const client_name = yield select((store) => store.common.clientProfile.name);
    const locale = yield call(getLocaleFlow);
    yield call(loading, function* () {
      const allergensInfo = yield call(api.getAllergensInfo, locale, client_name);
      yield put({
        type: SET_COMMON_PROP,
        key: 'allergensInfo',
        value: allergensInfo,
      });
    });
  }
};

/* firebase Saga */
export const firebaseFlow = function* () {
  while (true) {
    yield take(INIT_FIREBASE);
    if (Capacitor.platform !== 'web') {
      try {
        // Register with Apple / Google to receive push via APNS/FCM
        let result = { granted: false };
        try {
          result = yield call(PushNotifications.requestPermission);
        } catch (error) {
          result.granted = true;
        }
        if (result.granted) {
          yield call(PushNotifications.register);
          const firebaseChannel = eventChannel((emitter) => {
            PushNotifications.addListener('registration', (token) => {
              emitter({
                type: 'registration',
                value: token.value,
              });
            });

            // Some issue with your setup and push will not work
            PushNotifications.addListener('registrationError', (error) => {
              emitter({
                type: 'registrationError',
                value: error,
              });
            });

            // Show us the notification payload if the app is open on our device
            PushNotifications.addListener('pushNotificationReceived', (notification) => {
              emitter({
                type: 'pushNotificationReceived',
                value: notification,
              });
            });

            // Method called when tapping on a notification
            PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
              emitter({
                type: 'pushNotificationActionPerformed',
                value: notification,
              });
            });
          });

          while (true) {
            const firebaseMessage = yield take(firebaseChannel);
            // eslint-disable-next-line no-console
            console.log('firebase message', firebaseMessage);
            switch (firebaseMessage.type) {
              case 'registration': {
                yield put({
                  type: SET_COMMON_PROP,
                  key: 'deviceFcmToken',
                  value: firebaseMessage.value,
                });
                break;
              }
              default:
            }
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('INIT_FIREBASE_ERROR', error);
      }
    }
  }
};

const checkFibaseDBProperty = function* (propName, obj = {}) {
  let ret = false;
  const storagePropName = propName + '_firebase';
  if (obj[propName]) {
    const storagePropValue = yield call(asyncStorage.getItem, storagePropName);
    if (!storagePropValue || storagePropValue !== obj[propName]) {
      yield call(asyncStorage.setItem, storagePropName, obj[propName]);
      ret = true;
    }
  }
  return ret;
};

export const firebaseDatabaseFlow = function* () {
  while (true) {
    yield take(INIT_FIREBASE_DATABASE);
    yield call(firebaseDatabase);
  }
};

export const firebaseDatabase = function* () {
  const profileId = yield select((store) => store.profile.profile.id);
  const client_id = getConfig().client.client_id;
  const auth = yield select((store) => store.profile.auth);
  const firebaseChannel = eventChannel((emitter) => {
    const connection = !firebase.apps.length
      ? firebase.initializeApp(getConfig().firebaseConfig)
      : firebase.app();
    const restaurants = connection.database().ref('restaurants');
    const client = connection.database().ref(`client_${client_id.toString()}`);
    if (auth && auth.loggedIn) {
      const db = connection.database().ref(profileId);
      db.on('value', (snap) => {
        let db_record = snap.val();
        if (db_record) {
          emitter({ type: 'db', value: db_record });
        }
      });
    }

    restaurants.on('value', (snap) => {
      let db_record = snap.val();
      if (db_record) {
        emitter({ type: 'db', value: db_record });
      }
    });

    client.on('value', (snap) => {
      let db_record = snap.val();
      if (db_record) {
        emitter({ type: 'db', value: db_record });
      }
    });

    // unsubscribe function
    return () => {};
  });

  while (true) {
    const firebaseMessage = yield take(firebaseChannel);
    // eslint-disable-next-line no-console
    console.log('firebase database message', firebaseMessage);
    const { type, value } = firebaseMessage;
    let valueArray = Object.keys(value).map((key) => ({
      key,
      value: value[key],
    }));
    switch (type) {
      case 'db': {
        const auth = yield select((store) => store.profile.auth);
        if (auth && auth.loggedIn) {
          if (yield call(checkFibaseDBProperty, 'client_data', value)) {
            const client = yield call(api.getClient);
            document.title = client.buisiness_name;
            const favicon_image = client.favicon_image;
            const favicon = document.getElementById('favicon');
            favicon.href = favicon_image;
            yield put({
              type: SET_COMMON_PROP,
              key: 'clientProfile',
              value: client,
            });
            yield put({
              type: SET_COMMON_PROP,
              key: 'clientProfileUpdated',
              value: value.client_data,
            });
          }
          if (yield call(checkFibaseDBProperty, 'timestamp', value)) {
            yield put({ type: GET_PROFILE, skipLoading: true });
          }
          if (yield call(checkFibaseDBProperty, 'vouchers', value)) {
            // get vouhers here
            yield put({ type: GET_VOUCHERS });
          }
          if (yield call(checkFibaseDBProperty, 'order_history', value)) {
            yield put({ type: GET_ORDER_HISTORY, loading: false });
          }

          if (
            valueArray[valueArray.length - 2] &&
            (yield call(
              checkFibaseDBProperty,
              valueArray[valueArray.length - 2].key.toString(),
              value,
            ))
          ) {
            const lastOrderResult = yield call(
              api.getLastOrder,
              valueArray[valueArray.length - 2].key,
            );
            let orderHistory = yield select((store) => store.orders.orderHistory);
            let newOrderHistory = [...orderHistory];
            if (orderHistory && lastOrderResult) {
              newOrderHistory = newOrderHistory.map((order) => {
                if (order.id === lastOrderResult.id) {
                  return {
                    ...order,
                    ...lastOrderResult,
                  };
                } else {
                  return order;
                }
              });
            }
            yield put({
              type: SET_ORDERS_PROP,
              key: 'orderHistory',
              value: newOrderHistory,
            });
          }
        }
        if (yield call(checkFibaseDBProperty, 'restaurant_snooze_data', value)) {
          // get restaurants snoozed data
          yield put({ type: GET_RESTAURANTS_SNOOZED_DATA });
          yield put({
            type: SET_RESTAURANT_PROP,
            key: 'restaurantsUpdated',
            value: value.restaurant_snooze_data,
          });
        }
        break;
      }
      default:
    }
  }
};

export const saveFcmToken = function* () {
  const profile = yield select((store) => store.profile.profile);
  const deviceFcmToken = yield select((store) => store.common.deviceFcmToken);

  if (isDefined(profile) && isDefined(deviceFcmToken) && profile.fcm_token !== deviceFcmToken) {
    //save new fcm token
    yield put({
      type: UPDATE_PROFILE,
      data: { fcm_token: deviceFcmToken },
      skipAlert: true,
    });
  }
};

// fetch user profile (if exists) and translate using profile.locale OR using default locale form config
export const translateSaga = function* (text) {
  const store = yield select();
  let locale = getConfig().localization.defaultLocale;
  if (store.profile && store.profile.profile && store.profile.profile.locale) {
    locale = store.profile.profile.locale;
  }
  return translate(text, locale);
};

export const getLocaleFlow = function* () {
  const store = yield select();
  let locale = getConfig().localization.defaultLocale;
  if (store.profile && store.profile.profile && store.profile.profile.locale) {
    locale = store.profile.profile.locale;
  }
  return locale;
};

export const locationFlow = function* () {
  while (true) {
    const action = yield take(LOCATION);
    const store = yield select();
    const myLocation = action.value || store.common.myLocation;
    yield put({ type: SET_COMMON_PROP, key: 'myLocation', value: myLocation });
    const restaurants = store.restaurants.restaurants;
    if (restaurants) {
      restaurants.forEach((restaurant) => {
        if (restaurant.position) {
          const [lat, lng] = restaurant.position.split(',');
          restaurant.latitude = parseFloat(lat);
          restaurant.longitude = parseFloat(lng);
        } else {
          const lat = getConfig().services.google_maps.defaultLat;
          const lng = getConfig().services.google_maps.defaultLng;
          restaurant.position = lat + ',' + lng;
          restaurant.latitude = lat;
          restaurant.longitude = lng;
        }
      });
      const sortRestaurants = yield call(sortLocations, restaurants, myLocation);
      yield put({
        type: SET_RESTAURANT_PROP,
        key: 'restaurants',
        value: sortRestaurants,
      });
    }
  }
};

/* order minutes Saga */
export const getOrderProductionFlow = function* () {
  while (true) {
    yield take(GET_ORDER_PRODUCTION_MINS);
    const order_production_mins = yield call(api.getOrderProduction);
    yield put({
      type: SET_COMMON_PROP,
      key: 'orderProductionMins',
      value: order_production_mins,
    });
  }
};

export const startTimerFlow = function* () {
  while (true) {
    const action = yield take(START_TIMER);
    const timer = yield spawn(timerSaga, action.data, action.cb);
    yield put({ type: SET_COMMON_PROP, key: 'timer_saga', value: timer });
  }
};

export const stopTimerFlow = function* () {
  while (true) {
    yield take(STOP_TIMER);
    const store = yield select();
    if (isDefined(store.common.timer_saga)) {
      yield cancel(store.common.timer_saga);
      yield put({ type: SET_COMMON_PROP, key: 'timer_saga', value: null });
    }
  }
};

export const restartTimerFlow = function* () {
  while (true) {
    yield take(RESTART_TIMER);
    yield put({ type: SET_COMMON_PROP, key: 'restart_timer', value: true });
  }
};

export const pauseTimerFlow = function* () {
  while (true) {
    yield take(PAUSE_TIMER);
    yield put({ type: SET_COMMON_PROP, key: 'pause_timer', value: true });
  }
};

export const continueTimerFlow = function* () {
  while (true) {
    yield take(CONTINUE_TIMER);
    yield put({ type: SET_COMMON_PROP, key: 'pause_timer', value: false });
  }
};

const timerSaga = function* (stopTime, cb) {
  let timer = 0;
  while (true) {
    if (true) {
      try {
        const store = yield select();
        const restartFlag = store.common.restart_timer;
        const pauseFlag = store.common.pause_timer;
        if (restartFlag) {
          timer = 0;
          yield put({ type: SET_COMMON_PROP, key: 'restart_timer', value: false });
        }
        if (timer < stopTime) {
          if (!pauseFlag) timer = ++timer;
          yield delay(1000);
        } else {
          if (document.hidden) {
            yield put({ type: SET_COMMON_PROP, key: 'restart_timer', value: false });
            forwardTo('/kiosk-dashboard');
          } else {
            if (cb) yield call(cb);
            const data = yield take(CONTINUE_TIMER);
            if (data.action && data.action.cb) {
              yield call(data.action.cb);
            }
          }
          timer = 0;
        }
      } catch (error) {
        console.log('Timer error', error);
        return null;
      }
    }
  }
};
